import { ArrowRight, LockKeyhole, SearchX } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { formatExpiry } from "../components/options/formatExpiry";
import ProductShareButtons from "../components/ProductShareButtons";
import ProductShareDiscoverBand from "../components/ProductShareDiscoverBand";
import ProductShareGateModal from "../components/ProductShareGateModal";
import ProductUpdateNotificationsButton from "../components/notifications/ProductUpdateNotificationsButton";
import ProductShareHoldingsCard, {
  type ShareViewer,
  type TeaserHolding,
} from "../components/ProductShareHoldingsCard";
import { SHARE_CONTENT_CLASS_NAME } from "../components/productShareLayout";
import { productDetailPath } from "../components/ProductShareUnlockedHoldings";
import { useAuth } from "../contexts/AuthContext";
import { usePerformanceChartData } from "../hooks/usePerformanceChartData";
import { useProductShareGateFlow } from "../hooks/useProductShareGateFlow";
import {
  ResolvedOption,
  ResolvedStock,
  useProductSharePage,
  type UseProductSharePageResult,
} from "../hooks/useProductSharePage";
import {
  usePublicProductStats,
  type UsePublicProductStatsResult,
} from "../hooks/usePublicProductStats";
import { TrackingEvents, type TrackingEventProps } from "../services/trackingEvents";
import { trackingService } from "../services/trackingService";
import {
  type ProductShareAggregation,
  type ProductShareMetadata,
} from "../services/shareableLinksService";
import { formatNumber } from "../utils/numberUtils";

// Sign-up stays on the frontend origin serving this share page.
const GENERIC_SIGNUP_URL = "/signup?utm_source=shared_link&utm_medium=product_share";

function TrackedArea({
  section,
  trackingProps,
  children,
}: {
  section: "holdings" | "discovery" | "disclaimer";
  trackingProps: TrackingEventProps;
  children: React.ReactNode;
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const trackedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const trackingKey = [
      trackingProps.surface,
      trackingProps.load_state,
      trackingProps.product_type,
      trackingProps.provider_id,
      trackingProps.product_id,
      section,
    ].join(":");

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || trackedKeyRef.current === trackingKey) return;
      trackedKeyRef.current = trackingKey;
      trackingService.track(TrackingEvents.PRODUCT_SHARE_SECTION_VIEW, {
        ...trackingProps,
        section,
      });
      observer.disconnect();
    }, { threshold: 0.25 });

    observer.observe(element);
    return () => observer.disconnect();
  }, [section, trackingProps]);

  return <div ref={elementRef}>{children}</div>;
}

/**
 * The utm_* values that name this share link as where a sign-up came from.
 * They ride on the /signup link and, while the sign-up gate is on screen, on
 * every event as session tags: the gate creates the account on this page,
 * whose URL carries no utm_* of its own. Campaign and content are left out
 * when empty; the key order is the order the /signup link is built in.
 */
export function buildShareAttribution(
  metadata: ProductShareMetadata,
  aggregation: ProductShareAggregation
): Record<string, string> {
  const attribution: Record<string, string> = {
    utm_source: "shared_link",
    utm_medium: metadata.productType === "BASKET" ? "basket" : "portfolio",
  };
  if (metadata.providerName) attribution.utm_campaign = metadata.providerName;
  if (aggregation.title) attribution.utm_content = aggregation.title;
  return attribution;
}

function buildSignupUrl(
  metadata: ProductShareMetadata,
  aggregation: ProductShareAggregation
): string {
  const params = new URLSearchParams(buildShareAttribution(metadata, aggregation));
  return `/signup?${params.toString()}`;
}

/** Use only the securities already disclosed by the public endpoint. */
function buildTeaserHoldings(
  stocks: ResolvedStock[],
  options: ResolvedOption[]
): TeaserHolding[] {
  return [
    ...stocks.map(stock => ({
      key: `stock-${stock.conId}`, type: "Stock" as const,
      name: stock.symbol || String(stock.conId), description: stock.longName || "Stock position", weight: stock.weight,
    })),
    ...options.map(option => ({
      key: `option-${option.conId}`, type: "Option" as const,
      name: option.symbol || String(option.conId),
      description: [option.expiryDateLocal ? formatExpiry(option.expiryDateLocal) : null,
        option.strikePrice ? formatNumber(Number(option.strikePrice)) : null, option.putCall === "C" ? "Call" : "Put"].filter(Boolean).join(" "),
      weight: option.weight,
    })),
  ].slice(0, 3);
}

function totalHoldings(aggregation: ProductShareAggregation): number {
  if (aggregation.totalHoldings !== null) return aggregation.totalHoldings;
  return (aggregation.totalStocks ?? 0)
    + (aggregation.totalBaskets ?? 0)
    + (aggregation.totalOptions ?? 0);
}

function PageSkeleton() {
  return (
    <div className="min-h-screen min-h-dvh bg-v7-canvas font-v7-sans text-v7-ink transition-colors duration-300">
      <div className="mx-auto max-w-[1200px] animate-pulse space-y-6 px-4 py-8 sm:px-6 md:px-10 md:py-12">
        <div className="h-12 w-80 max-w-full rounded bg-v7-mist" />
        <div className="h-5 w-96 max-w-full rounded bg-v7-mist" />
        <div className="h-[260px] rounded-[14px] bg-v7-ink/10" />
        <div className="h-[320px] rounded-v7-panel border border-v7-ink/10 bg-v7-white" />
      </div>
    </div>
  );
}

const primaryButtonClassName = "inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-v7-control border border-v7-ink bg-v7-ink px-5 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark";
const ghostButtonClassName = "inline-flex min-h-11 items-center whitespace-nowrap rounded-v7-control border border-v7-ink/20 bg-v7-paper px-5 text-sm font-medium text-v7-ink-soft transition-colors hover:border-v7-bronze hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark";

function ShareHeader({
  viewer,
  productPath,
  signupPath,
  openGateModal,
  onCtaClick,
}: {
  viewer: ShareViewer;
  productPath: string | null;
  signupPath: string;
  openGateModal: ((ctaId: string) => void) | null;
  onCtaClick: (ctaId: string, destination: string) => void;
}) {
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-v7-ink/15 bg-v7-canvas pt-[env(safe-area-inset-top)]">
      <div className={`${SHARE_CONTENT_CLASS_NAME} flex min-h-16 items-center justify-between gap-2 md:min-h-[72px]`}>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onCtaClick("header_logo", "marketing_site")}
          className="flex min-h-11 items-center gap-2.5 rounded-v7-control focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
        >
          <img src="/favicon.png" alt="Plutus" className="h-[30px] w-[30px] object-contain" />
          <span aria-hidden="true" className="hidden text-sm font-medium uppercase tracking-[0.28em] text-v7-ink min-[360px]:inline max-[419px]:text-[13px] max-[419px]:tracking-[0.2em]">Plutus</span>
        </a>
        <div className="flex items-center gap-2 max-[419px]:[&_a]:px-3 max-[419px]:[&_button]:px-3">
          {viewer.state === "signed_in" && (
            <>
              <span className="hidden items-center gap-2.5 text-[13px] text-v7-ink/60 sm:inline-flex" data-testid="share-header-viewer">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-v7-ink/15 bg-v7-white font-v7-serif text-[13px] text-v7-ink">
                  {(viewer.email ?? "P")[0].toUpperCase()}
                </span>
                <span className="hidden max-w-[220px] truncate md:inline">{viewer.email}</span>
              </span>
              <Link to={viewer.landingPath} onClick={() => onCtaClick("header_open_app", "app")} className={primaryButtonClassName}>
                Open Plutus
                <ArrowRight className="h-[15px] w-[15px]" aria-hidden="true" />
              </Link>
            </>
          )}
          {viewer.state === "anonymous" && (
            <>
              <Link
                to="/login"
                state={{ from: productPath ?? location, fromProductShare: productPath !== null }}
                onClick={() => onCtaClick("header_login", "login")}
                className={ghostButtonClassName}
              >
                Log In
              </Link>
              {openGateModal ? (
                <button
                  type="button"
                  onClick={() => openGateModal("header_signup")}
                  className={primaryButtonClassName}
                >
                  <span>Sign Up Free</span>
                </button>
              ) : (
                <Link
                  to={signupPath}
                  state={{ from: productPath ?? location, fromProductShare: productPath !== null }}
                  onClick={() => onCtaClick("header_signup", "signup")}
                  className={primaryButtonClassName}
                >
                  <span>Sign Up Free</span>
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function TitleBlock({
  metadata,
  title,
  isSignedIn,
}: {
  metadata: ProductShareMetadata;
  title: string;
  isSignedIn: boolean;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
      <h1 className="min-w-0 max-w-[720px] text-balance text-[34px] font-light leading-[1.08] tracking-[-1.2px] text-v7-ink min-[821px]:text-5xl">
        {title}
      </h1>
      <div className="ml-auto flex shrink-0 items-center gap-3">
        {isSignedIn && (
          <ProductUpdateNotificationsButton
            productType={metadata.productType}
            productId={metadata.productId}
            visibility={metadata.visibility}
            appearance="alpha"
          />
        )}
        <ProductShareButtons
          title={title}
          size="sm"
          trackingProps={{
            resource_type: metadata.productType,
            resource_id: metadata.productId,
            provider_id: metadata.providerId,
            surface: "product_share_v2",
          }}
        />
      </div>
    </div>
  );
}

function Disclaimer({ onLinkClick }: { onLinkClick: () => void }) {
  return (
    <section className="pt-10 md:pt-[72px]">
      <p className="max-w-5xl text-[11px] leading-[17px] text-v7-ink/60">
        This content is provided by <a href="/" target="_blank" rel="noopener noreferrer" onClick={onLinkClick} className="text-v7-ink underline underline-offset-2 transition-colors hover:text-v7-bronze-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark">runplutus.com</a>. The RunPlutus technology platform may work with registered investment advisers that provide services which may be regulated by the SEC or other regulatory bodies. The information presented here is for informational purposes only and does not constitute investment advice, a recommendation, or an offer to buy or sell any securities. Past performance is not indicative of future results. All investments involve risk, including the possible loss of principal. We do not know your individual financial situation, objectives, or risk tolerance, and this information should not be relied upon as a substitute for personalized professional advice. Please consult a qualified financial adviser before making any investment decisions.
      </p>
    </section>
  );
}

function ProductUnavailableMessage({ notFound }: { notFound: boolean }) {
  return (
    <section className="rounded-v7-panel border border-v7-ink/15 bg-v7-white px-6 py-16 text-center">
      <SearchX className="mx-auto h-10 w-10 text-v7-ink/30" />
      <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.14em] text-v7-bronze-dark">Shared product</p>
      <h1 className="mx-auto mt-2 max-w-xl text-[32px] font-light leading-9 tracking-[-0.045em] text-v7-ink md:text-4xl md:leading-10">
        {notFound ? "This product could not be found" : "This product could not be loaded"}
      </h1>
      <p className="mx-auto mt-3 max-w-lg text-[15px] leading-[22px] text-v7-ink-soft">
        {notFound
          ? "The share link may be invalid or the product may no longer be available. Explore other portfolios available on Plutus below."
          : "We could not load this product right now. Please try again later, or explore other portfolios available on Plutus below."}
      </p>
    </section>
  );
}

/**
 * Fixture results for the dev-preview route. When set, the page renders
 * these instead of what the share hooks return; the chart and the signed-in
 * holdings still load from the API for the product the fixture names.
 */
export interface ProductSharePreview {
  share: UseProductSharePageResult;
  stats: UsePublicProductStatsResult;
}

const ProductSharePageV2: React.FC<{ preview?: ProductSharePreview }> = ({ preview }) => {
  const { token } = useParams<{ token: string }>();
  // Hooks always run; with no token the share hook settles to its error state
  // without a request, and the preview result takes precedence below.
  const liveShare = useProductSharePage(preview ? undefined : token);
  const liveStats = usePublicProductStats();
  const {
    metadata,
    aggregation,
    stocks,
    options,
    isLoading,
    error,
    errorType,
  } = preview?.share ?? liveShare;
  const {
    stats,
    providerNames,
    portfolioFactors,
    isLoading: statsLoading,
    error: statsError,
  } = preview?.stats ?? liveStats;
  const { user, isAuthenticated, isLoading: authLoading, getDefaultLandingPage } = useAuth();
  const defaultPeriodAppliedForProductRef = useRef<string | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    const previousValue = root.getAttribute("data-alpha-intercom-launcher-hidden");
    root.setAttribute("data-alpha-intercom-launcher-hidden", "true");
    return () => {
      if (previousValue === null) {
        root.removeAttribute("data-alpha-intercom-launcher-hidden");
      } else {
        root.setAttribute("data-alpha-intercom-launcher-hidden", previousValue);
      }
    };
  }, []);

  useEffect(() => {
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousThemeColor = themeMeta?.getAttribute("content") ?? null;
    const appRoot = document.getElementById("root");
    const backgroundElements = [document.documentElement, document.body, appRoot]
      .filter((element): element is HTMLElement => element !== null);
    const previousCanvasClasses = backgroundElements.map((element) =>
      element.classList.contains("bg-v7-canvas")
    );

    const previousOverflow = [document.documentElement, document.body].map(element => element.style.overflowX);
    [document.documentElement, document.body].forEach(element => { element.style.overflowX = "clip"; });
    backgroundElements.forEach((element) => element.classList.add("bg-v7-canvas"));
    const canvasColor = window.getComputedStyle(document.body).backgroundColor;
    if (themeMeta && canvasColor) themeMeta.setAttribute("content", canvasColor);

    return () => {
      [document.documentElement, document.body].forEach((element, index) => { element.style.overflowX = previousOverflow[index]; });
      backgroundElements.forEach((element, index) => {
        if (!previousCanvasClasses[index]) element.classList.remove("bg-v7-canvas");
      });
      if (themeMeta) {
        if (previousThemeColor === null) themeMeta.removeAttribute("content");
        else themeMeta.setAttribute("content", previousThemeColor);
      }
    };
  }, []);

  const isPrivate = metadata?.visibility === "PRIVATE";
  // The sign-up panel and modal exist for a loaded, non-private product.
  // Elsewhere anonymous CTAs go to /signup.
  const gateAvailable = !isLoading && !error && !!metadata && !!aggregation && !isPrivate;
  const productPath = !preview && !isLoading && !error && metadata && aggregation
    ? productDetailPath(metadata.productType, metadata.providerId, metadata.productId)
    : null;

  const viewer = useMemo<ShareViewer>(() => {
    if (isAuthenticated) {
      return { state: "signed_in", email: user?.email ?? null, landingPath: getDefaultLandingPage() };
    }
    return { state: authLoading ? "loading" : "anonymous", email: null, landingPath: "/alpha" };
  }, [authLoading, getDefaultLandingPage, isAuthenticated, user?.email]);
  const isViewerSignedIn = viewer.state === "signed_in";

  const chart = usePerformanceChartData({
    isActive: !!metadata && !isPrivate,
    entityId: metadata?.productId ?? 0,
    entityType: metadata?.productType ?? "BASKET",
    entityName: aggregation?.title ?? "",
    providerId: metadata?.providerId,
    dataSource: "v3-factors-public",
    entityCreatedAt: metadata?.createdAtUtc,
  });
  const selectChartPeriod = chart.handleSelectPeriod;
  const holdingTotal = aggregation ? totalHoldings(aggregation) : 0;
  const loadState = isLoading
    ? "loading"
    : error || !metadata || !aggregation
      ? errorType === "load-failed" ? "load_failed" : "not_found"
      : isPrivate ? "private" : "available";
  const trackingProps = useMemo<TrackingEventProps>(() => ({
    surface: "product_share_v2",
    load_state: loadState,
    product_id: metadata?.productId ?? null,
    product_type: metadata?.productType ?? null,
    provider_id: metadata?.providerId ?? null,
    visibility: metadata?.visibility ?? null,
    is_authenticated: isViewerSignedIn,
  }), [isViewerSignedIn, loadState, metadata]);
  const trackedViewKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || authLoading || (metadata && chart.loading)) return;
    const viewKey = metadata
      ? `${metadata.productType}:${metadata.providerId}:${metadata.productId}:${loadState}`
      : loadState;
    if (trackedViewKeyRef.current === viewKey) return;
    trackedViewKeyRef.current = viewKey;

    const ytdReturn = chart.timeframePerformance["Year to date"] ?? null;
    trackingService.track(TrackingEvents.PRODUCT_SHARE_VIEW, {
      ...trackingProps,
      default_period: ytdReturn === null ? "Since inception" : "Year to date",
      has_description: Boolean(aggregation?.description),
      holding_count: holdingTotal,
    });
  }, [
    aggregation?.description,
    authLoading,
    chart.loading,
    chart.timeframePerformance,
    holdingTotal,
    isLoading,
    loadState,
    metadata,
    trackingProps,
  ]);

  const trackCtaClick = useCallback((
    ctaId: string,
    destination = "signup",
    details: TrackingEventProps = {}
  ) => {
    trackingService.track(TrackingEvents.PRODUCT_SHARE_CTA_CLICK, {
      ...trackingProps,
      ...details,
      cta_id: ctaId,
      destination,
    });
  }, [trackingProps]);

  const gateOnScreen = gateAvailable && viewer.state === "anonymous";
  const flow = useProductShareGateFlow({
    enabled: gateOnScreen,
    productPath,
    total: holdingTotal,
    trackingProps,
    // This runs before the early returns below, so the product may not have loaded; the gate is off until it has.
    attribution: metadata && aggregation ? buildShareAttribution(metadata, aggregation) : {},
    onCtaClick: trackCtaClick,
  });
  const openGateModal = gateOnScreen ? flow.openModal : null;

  // Conversion CTAs send signed-in viewers into the app, anonymous viewers to
  // the sign-up modal on this page when it exists and to /signup otherwise.
  const conversionDestination = isViewerSignedIn ? "app" : openGateModal ? "gate_modal" : "signup";
  const trackSignupClick = useCallback((
    ctaId: string,
    details: TrackingEventProps = {}
  ) => {
    trackCtaClick(ctaId, conversionDestination, details);
  }, [conversionDestination, trackCtaClick]);

  const trackedChart = useMemo(() => ({
    ...chart,
    handleSelectPeriod: (period: string) => {
      if (period !== chart.selectedPeriod) {
        trackingService.track(TrackingEvents.PRODUCT_SHARE_PERIOD_CHANGE, {
          ...trackingProps,
          period,
        });
      }
      chart.handleSelectPeriod(period);
    },
  }), [chart, trackingProps]);

  // Default period: year to date when the product has one, otherwise since inception.
  useEffect(() => {
    if (!metadata || isPrivate || chart.loading) return;

    const ytdReturn = chart.timeframePerformance["Year to date"] ?? null;
    const inceptionReturn = chart.timeframePerformance["Since inception"]
      ?? chart.timeframePerformance.All
      ?? null;
    if (ytdReturn === null && inceptionReturn === null) return;

    const productKey = `${metadata.providerId}:${metadata.productType}:${metadata.productId}`;
    if (defaultPeriodAppliedForProductRef.current === productKey) return;

    defaultPeriodAppliedForProductRef.current = productKey;
    const defaultPeriod = ytdReturn === null ? "Since inception" : "Year to date";
    if (chart.selectedPeriod !== defaultPeriod) {
      void selectChartPeriod(defaultPeriod);
    }
  }, [
    chart.loading,
    chart.selectedPeriod,
    chart.timeframePerformance,
    isPrivate,
    metadata,
    selectChartPeriod,
  ]);

  const teaserHoldings = useMemo(
    () => buildTeaserHoldings(stocks, options),
    [stocks, options]
  );

  if (isLoading) return <PageSkeleton />;

  const discoverBand = (
    <TrackedArea section="discovery" trackingProps={trackingProps}>
      <ProductShareDiscoverBand
        metadata={metadata && aggregation ? metadata : null}
        stats={stats}
        providerNames={providerNames}
        portfolioFactors={portfolioFactors}
        isLoading={statsLoading}
        error={statsError}
        isSignedIn={isViewerSignedIn}
        openGateModal={openGateModal}
        signupPath={metadata && aggregation ? buildSignupUrl(metadata, aggregation) : GENERIC_SIGNUP_URL}
        onSignupClick={trackSignupClick}
      />
    </TrackedArea>
  );

  if (error || !metadata || !aggregation) {
    return (
      <div className="min-h-screen min-h-dvh overflow-x-hidden bg-v7-canvas font-v7-sans text-v7-ink transition-colors duration-300">
        <ShareHeader viewer={viewer} productPath={productPath} signupPath={GENERIC_SIGNUP_URL} openGateModal={null} onCtaClick={trackCtaClick} />
        <main className={`${SHARE_CONTENT_CLASS_NAME} pb-16 pt-7`}>
          <div className="mb-14"><ProductUnavailableMessage notFound={errorType !== "load-failed"} /></div>
          {discoverBand}
          <TrackedArea section="disclaimer" trackingProps={trackingProps}>
            <Disclaimer onLinkClick={() => trackCtaClick("disclaimer_link", "marketing_site")} />
          </TrackedArea>
        </main>
      </div>
    );
  }

  const signupUrl = buildSignupUrl(metadata, aggregation);

  return (
    <div
      data-testid="product-share-page"
      className="min-h-screen min-h-dvh overflow-x-hidden bg-v7-canvas font-v7-sans text-v7-ink transition-colors duration-300"
    >
      <ShareHeader viewer={viewer} productPath={productPath} signupPath={signupUrl} openGateModal={openGateModal} onCtaClick={trackCtaClick} />

      <main className={`${SHARE_CONTENT_CLASS_NAME} pb-[max(4rem,var(--share-gate-bar-height,0px))] pt-7 min-[821px]:pt-10`}>
        <TitleBlock metadata={metadata} title={aggregation.title} isSignedIn={isViewerSignedIn} />

        {isPrivate ? (
          <section className="mb-14 rounded-v7-panel border border-v7-ink/15 bg-v7-white p-8 text-center">
            <LockKeyhole className="mx-auto h-8 w-8 text-v7-bronze-dark" />
            <h2 className="mt-3 text-lg font-medium text-v7-ink">Private product</h2>
            <p className="mt-2 text-[15px] leading-[22px] text-v7-ink-soft">
              Performance and holdings are not disclosed on this public share link.
            </p>
          </section>
        ) : (
          <TrackedArea section="holdings" trackingProps={trackingProps}>
            <div className="mb-8">
              <ProductShareHoldingsCard
                metadata={metadata}
                title={aggregation.title}
                total={holdingTotal}
                holdings={teaserHoldings}
                viewer={viewer}
                chart={trackedChart}
                flow={flow}
                onCtaClick={trackCtaClick}
              />
            </div>
          </TrackedArea>
        )}

        {discoverBand}

        <TrackedArea section="disclaimer" trackingProps={trackingProps}>
          <Disclaimer onLinkClick={() => trackCtaClick("disclaimer_link", "marketing_site")} />
        </TrackedArea>
      </main>

      {openGateModal && (
        <ProductShareGateModal
          flow={flow}
          total={holdingTotal}
          onLoginClick={() => trackCtaClick("gate_login", "login")}
        />
      )}
    </div>
  );
};

export default ProductSharePageV2;
